import React, { useLayoutEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Flex } from "@chakra-ui/react";
import { useNavigationType, useParams } from "react-router";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import LandingPageHeader from "@/features/landing/components/Header";
import LandingFooter from "@/features/landing/landing-footer";
import usePathPickLandingCta from "@/features/pathpick/features/landing/hooks/usePathPickLandingCta";
import PathPickLandingHero from "@/features/pathpick/features/landing/components/PathPickLandingHero";
import PathPickLandingProblemSection from "@/features/pathpick/features/landing/components/PathPickLandingProblemSection";
import PathPickLandingSolutionSection from "@/features/pathpick/features/landing/components/PathPickLandingSolutionSection";
import PathPickLandingProductShowcaseSection from "@/features/pathpick/features/landing/components/PathPickLandingProductShowcaseSection";
import PathPickLandingComparisonSection from "@/features/pathpick/features/landing/components/PathPickLandingComparisonSection";
import PathPickLandingMembershipSection from "@/features/pathpick/features/landing/components/PathPickLandingMembershipSection";
import PathPickLandingFaqSection from "@/features/pathpick/features/landing/components/PathPickLandingFaqSection";
import PathPickLandingFloatingBar from "@/features/pathpick/features/landing/components/PathPickLandingFloatingBar";
import PathPickLandingCtaButton from "@/features/pathpick/features/landing/components/PathPickLandingCtaButton";
import {
  FAQ_ITEM_KEYS,
  PATHPICK_LANDING_CANONICAL_ORIGIN,
  PATHPICK_LANDING_COLORS,
  PATHPICK_LANDING_PATH,
} from "@/features/pathpick/features/landing/constants";

/**
 * 패스픽 랜딩(공개 페이지, `/pathpick-landing`).
 *
 * 로그인 전에도 열리는 페이지라 라우트에 레이아웃(인증 가드)을 붙이지 않는다.
 * 로그인/멤버십 상태에 따라 CTA 문구와 목적지만 갈린다 — 판정은 `usePathPickLandingCta` 한 곳에 있다.
 */
export default function PathPickLandingPage() {
  const { t } = useI18nHelper();
  const { locale } = useParams();
  const cta = usePathPickLandingCta();
  const navigationType = useNavigationType();

  // 메인 랜딩에서 SPA 전환으로 넘어오면 window 스크롤이 그대로 이어지므로 맨 위로 올린다.
  // 뒤로/앞으로 가기(POP)는 브라우저가 기억한 위치를 따른다.
  useLayoutEffect(() => {
    if (navigationType === "POP") return;
    window.scrollTo(0, 0);
  }, []);

  const canonicalUrl = `${PATHPICK_LANDING_CANONICAL_ORIGIN}${
    locale ? `/${locale}` : ""
  }${PATHPICK_LANDING_PATH}`;

  // FAQ 리치 결과용 구조화 데이터 — 화면과 같은 항목 목록을 쓴다.
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEM_KEYS.map((key) => ({
      "@type": "Question",
      name: t(`path_pick_landing:faq.items.${key}.question`),
      acceptedAnswer: {
        "@type": "Answer",
        text: t(`path_pick_landing:faq.items.${key}.answer`),
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>{t("path_pick_landing:title")}</title>
        <link rel={"canonical"} href={canonicalUrl} />
        <link
          rel={"alternate"}
          hrefLang={"ko"}
          href={`${PATHPICK_LANDING_CANONICAL_ORIGIN}/ko${PATHPICK_LANDING_PATH}`}
        />
        <link
          rel={"alternate"}
          hrefLang={"en"}
          href={`${PATHPICK_LANDING_CANONICAL_ORIGIN}${PATHPICK_LANDING_PATH}`}
        />
        <meta
          name={"description"}
          content={t("path_pick_landing:meta.description")}
        />
        <meta property={"og:type"} content={"website"} />
        <meta property={"og:url"} content={canonicalUrl} />
        <meta
          property={"og:title"}
          content={t("path_pick_landing:meta.title")}
        />
        <meta
          property={"og:description"}
          content={t("path_pick_landing:meta.description")}
        />
        <meta
          property={"twitter:title"}
          content={t("path_pick_landing:meta.title")}
        />
        <meta
          property={"twitter:description"}
          content={t("path_pick_landing:meta.description")}
        />
        <script type={"application/ld+json"}>{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <Box
        minWidth={"320px"}
        position={"relative"}
        wordBreak={"keep-all"}
        background={PATHPICK_LANDING_COLORS.surface}
      >
        <header>
          {/* 헤더의 "서비스로 이동"은 패스픽 랜딩에서만 목적지가 다르다 —
              가입 전이면 멤버십, 가입 후(무료 플랜 포함)면 상품 찾기. */}
          <LandingPageHeader
            isSignedIn={cta.isSignedIn}
            serviceHref={cta.serviceHref}
            // 배경이 흰 페이지라 로고·언어 버튼은 항상 어둡게, 경계는 흰 보더 대신 그림자로 낸다.
            isAlwaysDark
            hasBottomShadow
          />
        </header>
        <Box as={"main"}>
          <PathPickLandingHero cta={cta} />
          <PathPickLandingProblemSection />
          <PathPickLandingSolutionSection />
          <PathPickLandingProductShowcaseSection />
          <PathPickLandingComparisonSection />
          <PathPickLandingMembershipSection cta={cta} />
          <PathPickLandingFaqSection />

          {/* 플로팅 바가 떠 있는 상태(로그인·첫 결제 전)에서는 같은 CTA를 두 번 두지 않는다. */}
          {!cta.showFloatingBar && (
            <Flex
              justifyContent={"center"}
              paddingX={"20px"}
              paddingBottom={{ base: "56px", lg: "80px" }}
            >
              <PathPickLandingCtaButton
                action={cta.primary}
                icon={"package"}
                shape={"pill"}
                size={"lg"}
                isPending={!cta.isReady}
              />
            </Flex>
          )}
        </Box>
        <LandingFooter />
      </Box>

      <PathPickLandingFloatingBar cta={cta} />
    </>
  );
}
